import React from 'react';
import { DefaultButton, MessageBar, MessageBarType, PrimaryButton, Stack, TextField } from '@fluentui/react';
import { NavigateFunction, useNavigate } from 'react-router-dom';
import { ApplicationContext } from '../../contexts/ApplicationContext';
import { ICompany } from '../../models';
import useObject from '../../hooks/useObject';
import { useAuthenticationContext } from '../../hooks/useAuthenticationContext';
import { Role } from '../../utils/Enums';
import LoadingIndicator from '../../components/LoadingIndicator';
import ErrorDialog from '../../components/ErrorDialog';

interface ICompanyProps
{

}

export const Company: React.FunctionComponent<ICompanyProps> = (props: React.PropsWithChildren<ICompanyProps>) => {
    const navigate: NavigateFunction = useNavigate();
    const { Services } = React.useContext(ApplicationContext);
    const { isUserInRole } = useAuthenticationContext();
    const canEdit = isUserInRole(Role.Administrator);
    const { value: entity, updateValue: updateEntity, setValue, hasChanges } = useObject<ICompany>({} as ICompany);
    const [isLoading, setIsLoading] = React.useState<boolean>(true);
    const [isSaving, setIsSaving] = React.useState<boolean>(false);
    const [saved, setSaved] = React.useState<boolean>(false);
    const [errorDialogText, setErrorDialogText] = React.useState<string>('');
    const [showErrorDialog, setShowErrorDialog] = React.useState<boolean>(false);

    const openErrorDialog = (message: string) => {
        setErrorDialogText(message);
        setShowErrorDialog(true);
    };

    React.useEffect(() => {
        Fetch();
    }, []);

    const Fetch = async () => {
        setIsLoading(true);
        try {
            setValue(await Services!.Company.GetCompany());
        } catch (e) {
            openErrorDialog((e as Error).message);
        }
        setIsLoading(false);
    }

    // CVR-nr. skrives som 8 tegn i NETS-filen
    const cvrError = entity.cvr && !/^\d{8}$/.test(entity.cvr) ? "CVR-nr. skal være 8 cifre" : undefined;

    const Save = async () => {
        setIsSaving(true);
        const result = await Services?.Company.UpdateCompany(entity);
        setIsSaving(false);

        if(result === "OK")
        {
            setSaved(true);
            Fetch();
        } else
        {
            openErrorDialog(result as string);
        }
    }

    const update = (change: Partial<ICompany>) => {
        setSaved(false);
        updateEntity(change);
    }

    if(isLoading) return <LoadingIndicator label="Henter vindmøllelauget..." />

    return <div>
                <h3>Vindmøllelauget</h3>
                <Stack tokens={{ childrenGap: 10 }} styles={{ root: { maxWidth: 600 } }}>
                    <Stack horizontal tokens={{ childrenGap: 10 }}>
                        <TextField label="Kort navn" value={entity.shortName ?? ""} maxLength={50} readOnly={!canEdit} styles={{ root: { width: 180 } }} onChange={(ev, newValue) => update({ shortName: newValue })} />
                        <TextField label="Navn" value={entity.name ?? ""} maxLength={200} readOnly={!canEdit} styles={{ root: { flexGrow: 1 } }} onChange={(ev, newValue) => update({ name: newValue })} />
                    </Stack>
                    <TextField label="CVR-nr." value={entity.cvr ?? ""} maxLength={8} readOnly={!canEdit} errorMessage={cvrError} styles={{ root: { width: 180 } }} onChange={(ev, newValue) => update({ cvr: (newValue ?? "").replace(/\D/g, "") })} />
                    <TextField label="Adresse" value={entity.address ?? ""} maxLength={200} readOnly={!canEdit} onChange={(ev, newValue) => update({ address: newValue })} />
                    <TextField label="Postnr. og by" value={entity.postalCodeCity ?? ""} maxLength={200} readOnly={!canEdit} onChange={(ev, newValue) => update({ postalCodeCity: newValue })} />
                    <TextField label="Kontaktperson (att.)" value={entity.attention ?? ""} maxLength={50} readOnly={!canEdit} onChange={(ev, newValue) => update({ attention: newValue })} />
                    <Stack horizontal tokens={{ childrenGap: 10 }}>
                        <TextField label="Telefon" value={entity.phone ?? ""} maxLength={50} readOnly={!canEdit} styles={{ root: { flexGrow: 1 } }} onChange={(ev, newValue) => update({ phone: newValue })} />
                        <TextField label="Telefax" value={entity.fax ?? ""} maxLength={50} readOnly={!canEdit} styles={{ root: { flexGrow: 1 } }} onChange={(ev, newValue) => update({ fax: newValue })} />
                    </Stack>
                    <TextField label="E-mail" type="email" value={entity.email ?? ""} maxLength={200} readOnly={!canEdit} onChange={(ev, newValue) => update({ email: newValue })} />
                    <TextField label="Hjemmeside" value={entity.website ?? ""} maxLength={200} readOnly={!canEdit} onChange={(ev, newValue) => update({ website: newValue })} />

                    {saved && <MessageBar messageBarType={MessageBarType.success} onDismiss={() => setSaved(false)}>Vindmøllelauget er gemt</MessageBar>}

                    <Stack horizontal tokens={{ childrenGap: 10 }}>
                        {canEdit && <PrimaryButton text={isSaving ? "Gemmer..." : "Gem ændringer"} disabled={!hasChanges || cvrError !== undefined || isSaving} onClick={() => Save()} />}
                        <DefaultButton text="Tilbage" onClick={() => { navigate("/indstillinger"); }} />
                    </Stack>
                </Stack>
                <ErrorDialog
                    isOpen={showErrorDialog}
                    errorText={errorDialogText}
                    onDismiss={() => setShowErrorDialog(false)}
                />
           </div>
}

export default Company;
